import { useEffect, useMemo, useState } from 'react';
import {
  collection, query, orderBy, onSnapshot, addDoc, doc, serverTimestamp, getDocs,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { portalOf } from '../contexts/MarketContext';
import { RESOURCE_CATEGORIES, expiryState, formatBytes, formatDateShort } from '../pages/staff/Resources';

// Over this, the Cloud Function sends download links instead of attachments so
// the email doesn't bounce off a school's mail server.
const ATTACH_LIMIT_BYTES = 10 * 1024 * 1024;

/**
 * Words in a resource's title that hint at when it's relevant, mapped to the
 * words that would show up in an itinerary. Used only to pre-tick the obvious
 * ones on first load — staff tick and untick freely from there.
 */
const SUGGESTION_HINTS = [
  { titleHint: /coach|bus/i,            itinerary: /coach|bus|transfer/i },
  { titleHint: /train|tram/i,           itinerary: /train|tram|metro|rail/i },
  { titleHint: /theme park|waterpark/i, itinerary: /theme park|waterpark|aqua|terra m|water park/i },
  { titleHint: /coastal/i,              itinerary: /beach|coast|playa|sea/i },
  { titleHint: /swim|pool/i,            itinerary: /swim|pool|beach|piscina/i },
  { titleHint: /cultural/i,             itinerary: /museum|cathedral|castle|cultural|old town/i },
  { titleHint: /entertainment/i,        itinerary: /bowling|cinema|arcade|entertainment|show/i },
  { titleHint: /city/i,                 itinerary: /city|centre|center|shopping/i },
];

function defaultFormat(r) {
  return r.pdfUrl ? 'pdf' : 'docx';
}

function fileFor(r, format) {
  const useFormat = r[`${format}Url`] ? format : defaultFormat(r);
  return {
    format: useFormat,
    url: r[`${useFormat}Url`] || null,
    name: r[`${useFormat}Name`] || '',
    size: r[`${useFormat}Size`] || 0,
  };
}

/**
 * The resource pack for one tour: tick the risk assessments / certificates /
 * forms this group needs and email them, with a record of exactly what went out.
 *
 * readOnly — tour leaders get the same library to read on the ground, without
 * the ability to send anything to the group.
 */
export default function ResourcePack({ requestId, request, readOnly = false }) {
  const { currentUser } = useAuth();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sent, setSent] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [formats, setFormats] = useState({});
  const [seeded, setSeeded] = useState(false);
  const [itineraryText, setItineraryText] = useState(null);

  const [recipients, setRecipients] = useState('');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [sendOk, setSendOk] = useState('');

  const portal = portalOf(request);

  // ── Library, scoped to this tour's portal ──────────────────────────────────
  useEffect(() => {
    const unsub = onSnapshot(query(collection(db, 'resources'), orderBy('title', 'asc')), (snap) => {
      const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setResources(all.filter((r) => !r.portals || r.portals.length === 0 || r.portals.includes(portal)));
      setLoading(false);
    });
    return unsub;
  }, [portal]);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'requests', requestId, 'sentResources'), orderBy('sentAt', 'desc')),
      (snap) => setSent(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    return unsub;
  }, [requestId]);

  useEffect(() => {
    setRecipients(request?.customerEmail || '');
  }, [request?.customerEmail]);

  // Itinerary text, read once, purely to pre-tick the obvious resources.
  useEffect(() => {
    if (readOnly) { setItineraryText(''); return; }
    let cancelled = false;
    getDocs(collection(db, 'requests', requestId, 'itinerary'))
      .then((snap) => {
        if (cancelled) return;
        const text = snap.docs
          .map((d) => {
            const it = d.data();
            return [it.title, it.description, it.type, it.location, it.notes].filter(Boolean).join(' ');
          })
          .join(' ')
          .toLowerCase();
        setItineraryText(text);
      })
      .catch(() => { if (!cancelled) setItineraryText(''); });
    return () => { cancelled = true; };
  }, [requestId, readOnly]);

  // Seed the initial tick list once both the library and the itinerary are in.
  useEffect(() => {
    if (seeded || readOnly || loading || itineraryText === null) return;
    const initial = new Set();
    for (const r of resources) {
      if (r.defaultInclude) { initial.add(r.id); continue; }
      const hint = SUGGESTION_HINTS.find((h) => h.titleHint.test(r.title || ''));
      if (hint && itineraryText && hint.itinerary.test(itineraryText)) initial.add(r.id);
    }
    setSelected(initial);
    setSeeded(true);
  }, [seeded, readOnly, loading, itineraryText, resources]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const r of resources) {
      const key = r.category || 'Other';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    }
    const ordered = [...RESOURCE_CATEGORIES, ...[...map.keys()].filter((k) => !RESOURCE_CATEGORIES.includes(k))];
    return ordered.filter((c) => map.has(c)).map((c) => [c, map.get(c)]);
  }, [resources]);

  const selectedResources = resources.filter((r) => selected.has(r.id));
  const totalBytes = selectedResources.reduce((sum, r) => sum + (fileFor(r, formats[r.id] || defaultFormat(r)).size || 0), 0);
  const willAttach = totalBytes > 0 && totalBytes <= ATTACH_LIMIT_BYTES;
  const expiredSelected = selectedResources.filter((r) => expiryState(r.expiryDate)?.level === 'expired');

  function toggle(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleCategory(items, on) {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const r of items) { if (on) next.add(r.id); else next.delete(r.id); }
      return next;
    });
  }

  function selectAll(on) {
    setSelected(on ? new Set(resources.map((r) => r.id)) : new Set());
  }

  async function handleSend() {
    if (sending || selected.size === 0) return;
    const to = recipients.split(',').map((s) => s.trim()).filter(Boolean);
    if (to.length === 0) { setSendError('Add at least one recipient email address.'); return; }
    if (expiredSelected.length > 0
      && !window.confirm(`${expiredSelected.map((r) => `"${r.title}"`).join(', ')} ${expiredSelected.length === 1 ? 'has' : 'have'} expired. Send anyway?`)) {
      return;
    }

    setSending(true);
    setSendError('');
    setSendOk('');
    try {
      const items = selectedResources.map((r) => {
        const f = fileFor(r, formats[r.id] || defaultFormat(r));
        return { resourceId: r.id, title: r.title || '', format: f.format };
      });
      const created = await addDoc(collection(db, 'resourceSendRequests'), {
        requestId,
        items,
        to,
        note: note.trim(),
        status: 'pending',
        requestedBy: currentUser?.email || '',
        requestedAt: serverTimestamp(),
      });

      // Watch the trigger doc so staff see the real outcome, not just "queued".
      await new Promise((resolve) => {
        const timeout = setTimeout(() => {
          setSendOk('Still sending — this can take a minute for large packs. Check Sent Resources below.');
          unsub(); resolve();
        }, 45000);
        const unsub = onSnapshot(doc(db, 'resourceSendRequests', created.id), (snap) => {
          const status = snap.data()?.status;
          if (status === 'sent') {
            setSendOk(`Sent to ${to.join(', ')}.`);
            setNote('');
            clearTimeout(timeout); unsub(); resolve();
          } else if (status === 'failed') {
            setSendError(snap.data()?.error || 'Send failed. Please try again.');
            clearTimeout(timeout); unsub(); resolve();
          }
        });
      });
    } catch (err) {
      console.error('Resource send failed:', err);
      setSendError('Could not queue that send. Please try again.');
    } finally {
      setSending(false);
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-8 text-center">
        <p className="text-[13px] text-[#9CA3AF]">Loading resources…</p>
      </div>
    );
  }

  if (resources.length === 0) {
    return (
      <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] p-12 text-center">
        <p className="text-[14px] font-bold text-[#111827] mb-1">No resources available</p>
        <p className="text-[13px] text-[#9CA3AF]">
          {readOnly
            ? 'The office has not published any documents for this tour yet.'
            : 'Add risk assessments and certificates in Resources, then they appear here to send.'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* ── Library ── */}
      <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 px-[18px] py-[14px] border-b border-[#F1F3F6]">
          <div>
            <h3 className="text-[14.5px] font-bold text-[#111827]">
              {readOnly ? 'Tour Documents' : 'Send Resources'}
            </h3>
            <p className="text-[12px] text-[#6B7280] mt-0.5">
              {readOnly
                ? 'Risk assessments, certificates and guidance for this tour.'
                : 'Tick what this group needs. Anything already ticked was suggested from the itinerary — change it freely.'}
            </p>
          </div>
          {!readOnly && (
            <div className="flex items-center gap-3">
              <button onClick={() => selectAll(true)} className="text-[12.5px] font-bold text-pb hover:text-navy transition">Select all</button>
              <button onClick={() => selectAll(false)} className="text-[12.5px] font-bold text-[#9CA3AF] hover:text-[#6B7280] transition">Clear</button>
            </div>
          )}
        </div>

        <div className="divide-y divide-[#F1F3F6]">
          {grouped.map(([category, items]) => {
            const allOn = items.every((r) => selected.has(r.id));
            return (
              <div key={category}>
                <div className="flex items-center justify-between px-[18px] py-[9px] bg-surface">
                  <p className="text-[11px] font-extrabold text-[#6B7280] uppercase tracking-[0.7px]">{category}</p>
                  {!readOnly && (
                    <button
                      onClick={() => toggleCategory(items, !allOn)}
                      className="text-[11.5px] font-bold text-pb hover:text-navy transition"
                    >
                      {allOn ? 'Untick all' : 'Tick all'}
                    </button>
                  )}
                </div>
                {items.map((r) => {
                  const format = formats[r.id] || defaultFormat(r);
                  const f = fileFor(r, format);
                  const exp = expiryState(r.expiryDate);
                  const on = selected.has(r.id);
                  return (
                    <div key={r.id} className="flex flex-wrap items-center gap-3 px-[18px] py-[12px] hover:bg-[#FAFBFC] transition">
                      {!readOnly && (
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() => toggle(r.id)}
                          className="w-[17px] h-[17px] rounded border-[#D1D5DB] text-pb cursor-pointer flex-shrink-0"
                        />
                      )}
                      <div className="flex-1 min-w-[200px]">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-[13px] font-semibold text-[#111827]">{r.title}</p>
                          {r.version && (
                            <span className="text-[10.5px] font-bold bg-surface text-[#6B7280] px-[7px] py-[2px] rounded-[5px]">{r.version}</span>
                          )}
                          {exp && exp.level !== 'ok' && (
                            <span className={`text-[10.5px] font-bold px-[7px] py-[2px] rounded-[5px] ${
                              exp.level === 'expired' ? 'bg-red-light text-dr' : 'bg-[#FEF3C7] text-[#92400E]'
                            }`}>
                              {exp.label}
                            </span>
                          )}
                        </div>
                        {r.description && <p className="text-[11.5px] text-[#6B7280] mt-0.5">{r.description}</p>}
                        {r.reviewedDate && (
                          <p className="text-[11px] text-[#9CA3AF] mt-0.5">Reviewed {formatDateShort(r.reviewedDate)}</p>
                        )}
                      </div>

                      {/* Format picker — only where both versions exist */}
                      {r.pdfUrl && r.docxUrl ? (
                        <div className="flex rounded-[7px] border border-[#E5E7EB] overflow-hidden flex-shrink-0">
                          {['pdf', 'docx'].map((fmt) => (
                            <button
                              key={fmt}
                              onClick={() => setFormats((p) => ({ ...p, [r.id]: fmt }))}
                              className={`px-[10px] py-[5px] text-[11px] font-extrabold transition ${
                                format === fmt ? 'bg-pb text-white' : 'bg-white text-[#9CA3AF] hover:text-[#6B7280]'
                              }`}
                            >
                              {fmt === 'pdf' ? 'PDF' : 'WORD'}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <span className="text-[10.5px] font-extrabold text-[#9CA3AF] flex-shrink-0">
                          {f.format === 'pdf' ? 'PDF' : 'WORD'}
                        </span>
                      )}

                      <span className="text-[11px] text-[#9CA3AF] w-[58px] text-right flex-shrink-0">{formatBytes(f.size)}</span>

                      {f.url && (
                        <a href={f.url} target="_blank" rel="noopener noreferrer"
                          className="flex-shrink-0 inline-flex items-center gap-1.5 px-[10px] py-[6px] text-[12px] font-semibold text-[#1A4A8A] bg-blue-light hover:bg-[#DCE7F5] rounded-[8px] transition">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                          Open
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Send bar ── */}
      {!readOnly && (
        <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] p-[18px]">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-[14px]">
            <h3 className="text-[14.5px] font-bold text-[#111827]">
              {selected.size} document{selected.size !== 1 ? 's' : ''} selected
              {totalBytes > 0 && <span className="text-[12.5px] font-semibold text-[#9CA3AF] ml-2">{formatBytes(totalBytes)}</span>}
            </h3>
            {selected.size > 0 && (
              <span className={`text-[11.5px] font-bold px-[9px] py-[4px] rounded-[20px] ${
                willAttach ? 'bg-green-light text-sg' : 'bg-[#FEF3C7] text-[#92400E]'
              }`}>
                {willAttach ? 'Sent as attachments' : 'Too large to attach — sent as download links'}
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-[6px]">Send to</label>
              <input
                type="text"
                value={recipients}
                onChange={(e) => setRecipients(e.target.value)}
                placeholder="name@school.org, evc@school.org"
                className="w-full border border-[#E5E7EB] rounded-[9px] px-3 py-[9px] text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
              />
              <p className="text-[11px] text-[#9CA3AF] mt-1">Defaults to the tour contact. Separate several with commas.</p>
            </div>
            <div>
              <label className="block text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-[6px]">Note (optional)</label>
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. For your EVC submission — shout if you need anything else."
                className="w-full border border-[#E5E7EB] rounded-[9px] px-3 py-[9px] text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
              />
            </div>
          </div>

          {sendError && <p className="text-[12.5px] text-dr mt-3">{sendError}</p>}
          {sendOk && <p className="text-[12.5px] text-sg font-semibold mt-3">{sendOk}</p>}

          <button
            onClick={handleSend}
            disabled={sending || selected.size === 0}
            className="mt-4 px-[18px] py-[10px] text-[13px] font-bold text-white bg-pb hover:bg-navy rounded-[9px] transition disabled:opacity-60"
          >
            {sending ? 'Sending…' : `Send ${selected.size || ''} document${selected.size !== 1 ? 's' : ''}`.replace('  ', ' ')}
          </button>
        </div>
      )}

      {/* ── What has been sent ── */}
      <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] overflow-hidden">
        <div className="flex items-center justify-between px-[18px] py-[14px] border-b border-[#F1F3F6]">
          <h3 className="text-[14.5px] font-bold text-[#111827]">Sent Resources</h3>
          <span className="text-[12px] text-[#6B7280]">{sent.length} send{sent.length !== 1 ? 's' : ''}</span>
        </div>
        {sent.length === 0 ? (
          <div className="px-[18px] py-10 text-center">
            <p className="text-[13px] text-[#9CA3AF]">Nothing has been sent to this group yet.</p>
          </div>
        ) : (
          <div className="divide-y divide-[#F1F3F6]">
            {sent.map((s) => (
              <div key={s.id} className="px-[18px] py-[13px]">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-[12.5px] font-bold text-[#111827]">
                    {(s.items || []).length} document{(s.items || []).length !== 1 ? 's' : ''} · {s.mode === 'links' ? 'download links' : 'attached'}
                  </p>
                  <p className="text-[11.5px] text-[#9CA3AF]">
                    To {(s.to || []).join(', ') || '—'}
                    {s.sentAt?.toDate && ` · ${s.sentAt.toDate().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`}
                    {s.sentBy && ` · by ${s.sentBy}`}
                  </p>
                </div>
                {s.note && <p className="text-[11.5px] text-[#6B7280] italic mt-1">“{s.note}”</p>}
                <div className="flex flex-wrap gap-2 mt-2">
                  {(s.items || []).map((item, i) => (
                    item.downloadUrl ? (
                      <a key={i} href={item.downloadUrl} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-[9px] py-[4px] text-[11.5px] font-semibold text-[#1A4A8A] bg-blue-light hover:bg-[#DCE7F5] rounded-[7px] transition">
                        {item.title} <span className="text-[10px] font-extrabold opacity-70">{item.format === 'docx' ? 'WORD' : 'PDF'}</span>
                      </a>
                    ) : (
                      <span key={i} className="inline-flex items-center gap-1.5 px-[9px] py-[4px] text-[11.5px] font-semibold text-[#9CA3AF] bg-surface rounded-[7px]">
                        {item.title}
                      </span>
                    )
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
